import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../../components/ui/button';

export default function Terms() {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <img src="/edrano-logo-dark.png" alt="Edrano" className="w-8 h-8 rounded-lg" />
              <span className="font-bold text-lg text-slate-900">Edrano</span>
            </Link>
            <Link to="/">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="py-16" data-testid="terms-content">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Terms of Service</h1>
          <p className="text-slate-500 mb-8">Last updated: February 12, 2026</p>

          <div className="prose prose-slate max-w-none">
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">1. Agreement to Terms</h2>
              <p className="text-slate-600 leading-relaxed">
                By accessing or using Edrano's services, you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our services.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">2. Description of Service</h2>
              <p className="text-slate-600 leading-relaxed">
                Edrano provides a white-label client portal platform that enables businesses to manage clients, sell services, and process payments. Our service includes web-based access to our platform and related features as described in your subscription plan.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">3. Account Registration</h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                To use our services, you must:
              </p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li>Be at least 18 years old or have legal authority to enter into contracts</li>
                <li>Provide accurate and complete registration information</li>
                <li>Maintain the security of your account credentials</li>
                <li>Notify us immediately of any unauthorized account access</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">4. Subscription and Payment</h2>
              
              <h3 className="text-lg font-medium text-slate-800 mb-2">4.1 Billing</h3>
              <p className="text-slate-600 leading-relaxed mb-4">
                Subscriptions are billed in advance on a monthly or annual basis. You agree to pay all applicable fees and taxes. All fees are non-refundable except as required by law.
              </p>
              
              <h3 className="text-lg font-medium text-slate-800 mb-2">4.2 Free Trial</h3>
              <p className="text-slate-600 leading-relaxed mb-4">
                We may offer free trials. At the end of the trial, your subscription will automatically convert to a paid plan unless you cancel before the trial ends.
              </p>
              
              <h3 className="text-lg font-medium text-slate-800 mb-2">4.3 Plan Changes</h3>
              <p className="text-slate-600 leading-relaxed">
                You may upgrade or downgrade your plan at any time. Changes take effect at the next billing cycle. Downgrades may result in loss of features or data limits.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">5. Acceptable Use</h2>
              <p className="text-slate-600 leading-relaxed mb-4">You agree not to:</p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li>Use the service for any illegal purpose</li>
                <li>Violate any laws or regulations</li>
                <li>Infringe on intellectual property rights</li>
                <li>Transmit malware, viruses, or harmful code</li>
                <li>Attempt to gain unauthorized access to our systems</li>
                <li>Interfere with other users' use of the service</li>
                <li>Use the service to send spam or unsolicited communications</li>
                <li>Resell or redistribute the service without authorization</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">6. Your Data</h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                You retain ownership of all data you upload to the platform ("Your Data"). By using our service, you grant us a limited license to use Your Data solely to provide the service to you.
              </p>
              <p className="text-slate-600 leading-relaxed">
                You are responsible for maintaining backups of Your Data. We provide data export features to help you retrieve your information.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">7. Intellectual Property</h2>
              <p className="text-slate-600 leading-relaxed">
                The Edrano platform, including all software, design, logos, and content, is owned by us and protected by intellectual property laws. Your subscription grants you a limited, non-exclusive license to use our platform as permitted by your plan.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">8. Third-Party Services</h2>
              <p className="text-slate-600 leading-relaxed">
                Our platform integrates with third-party services (e.g., Stripe for payments). Your use of these services is subject to their terms and conditions. We are not responsible for third-party services.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">9. Limitation of Liability</h2>
              <p className="text-slate-600 leading-relaxed">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, EDRANO SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY OR INDIRECTLY.
              </p>
              <p className="text-slate-600 leading-relaxed mt-4">
                Our total liability shall not exceed the amount you paid us in the 12 months preceding the claim.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">10. Disclaimer of Warranties</h2>
              <p className="text-slate-600 leading-relaxed">
                THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">11. Termination</h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                You may cancel your subscription at any time through your account settings. We may terminate or suspend your access immediately if you violate these terms.
              </p>
              <p className="text-slate-600 leading-relaxed">
                Upon termination, your right to use the service ceases. You may request export of Your Data within 30 days of termination.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">12. Changes to Terms</h2>
              <p className="text-slate-600 leading-relaxed">
                We may modify these terms at any time. We will notify you of material changes via email or through the platform. Continued use after changes constitutes acceptance of the new terms.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">13. Governing Law</h2>
              <p className="text-slate-600 leading-relaxed">
                These terms are governed by the laws of Greece. Any disputes shall be resolved in the courts of Athens, Greece.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">14. Contact</h2>
              <p className="text-slate-600 leading-relaxed">
                For questions about these Terms of Service, contact us at:
              </p>
              <div className="mt-4 p-4 bg-slate-100 rounded-lg">
                <p className="text-slate-700">
                  <strong>Email:</strong> legal@edrano.my<br />
                  <strong>Address:</strong> Athens, Greece
                </p>
              </div>
            </section>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 bg-slate-100 border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-slate-500">
              © {new Date().getFullYear()} Edrano. All rights reserved.
            </p>
            <div className="flex gap-6">
              <Link to="/privacy" className="text-sm text-slate-500 hover:text-indigo-600">Privacy</Link>
              <Link to="/gdpr" className="text-sm text-slate-500 hover:text-indigo-600">GDPR</Link>
              <Link to="/cookies" className="text-sm text-slate-500 hover:text-indigo-600">Cookies</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
